import { Button, Flex, HStack, Input, ListItem, Text, UnorderedList, VStack } from '@chakra-ui/react'
import { arrayMove } from '@dnd-kit/sortable'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { ActionButton } from '../ActionButton'
import { DragHandle, SortableList, useSortableRow } from '../SortableList'

export interface IListInputProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  /** Most items the list accepts. */
  maxItems?: number
  /** Most characters per item. */
  maxLength?: number
  isDisabled?: boolean
  /** Renders the items as a plain bulleted list. */
  isReadOnly?: boolean
  /** Shown when the list is empty. */
  emptyText?: string
}

const newId = () => crypto.randomUUID()

/**
 * A list of short texts built one item at a time: type, press Enter (or "Adicionar") and the text
 * becomes a topic. Topics can be edited in place, removed and dragged into a new order.
 *
 * Wrap it in a `FormField`: the label, `isRequired` and the list's error come from there, and the
 * typing box takes the field's id.
 */
export default function ListInput({
  value,
  onChange,
  placeholder = 'Digite um item e pressione Enter',
  maxItems,
  maxLength,
  isDisabled = false,
  isReadOnly = false,
  emptyText = 'Nenhum item adicionado'
}: IListInputProps) {
  const [draft, setDraft] = useState('')
  // Drag needs a stable id per row; texts can repeat, so they cannot be the id.
  const [ids, setIds] = useState<string[]>(() => value.map(newId))
  // The last list this component handed to `onChange`: any other `value` came from outside.
  const emitted = useRef(value)

  useEffect(() => {
    if (value === emitted.current) return
    const sameItems =
      value.length === emitted.current.length && value.every((item, index) => item === emitted.current[index])
    emitted.current = value
    // A list from outside (a reset, a load, another role's lists) gets fresh ids, which also
    // closes a row being edited; the same items keep theirs.
    if (!sameItems) setIds(value.map(newId))
  }, [value])

  const emit = (next: string[]) => {
    emitted.current = next
    onChange(next)
  }

  if (isReadOnly) {
    return value.length === 0 ? (
      <Text variant="caption">{emptyText}</Text>
    ) : (
      <UnorderedList spacing={1} pl={1}>
        {value.map((item, index) => (
          <ListItem key={index}>{item}</ListItem>
        ))}
      </UnorderedList>
    )
  }

  const isFull = maxItems !== undefined && value.length >= maxItems
  const canAdd = draft.trim() !== '' && !isFull && !isDisabled

  const add = () => {
    if (!canAdd) return
    emit([...value, draft.trim()])
    setIds([...ids, newId()])
    setDraft('')
  }

  const update = (index: number, text: string) => emit(value.map((item, i) => (i === index ? text : item)))

  const remove = (index: number) => {
    emit(value.filter((_, i) => i !== index))
    setIds(ids.filter((_, i) => i !== index))
  }

  const reorder = (from: number, to: number) => {
    emit(arrayMove(value, from, to))
    setIds(arrayMove(ids, from, to))
  }

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={2} align="flex-start">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder={isFull ? `Limite de ${maxItems} itens atingido` : placeholder}
          maxLength={maxLength}
          isDisabled={isDisabled || isFull}
          // The list is what is required, not this box: an empty box with items above is fine.
          isRequired={false}
        />
        <Button onClick={add} isDisabled={!canAdd} variant="outline" colorScheme="gray" flexShrink={0}>
          Adicionar
        </Button>
      </HStack>

      {value.length === 0 ? (
        <Text variant="caption">{emptyText}</Text>
      ) : (
        <SortableList ids={ids} onReorder={reorder}>
          <VStack align="stretch" spacing={1}>
            {value.map((item, index) => (
              <ListInputRow
                key={ids[index] ?? index}
                id={ids[index] ?? String(index)}
                text={item}
                maxLength={maxLength}
                isDisabled={isDisabled}
                onSave={(text) => update(index, text)}
                onRemove={() => remove(index)}
              />
            ))}
          </VStack>
        </SortableList>
      )}
    </VStack>
  )
}

interface IListInputRowProps {
  id: string
  text: string
  maxLength?: number
  isDisabled: boolean
  onSave: (text: string) => void
  onRemove: () => void
}

function ListInputRow({ id, text, maxLength, isDisabled, onSave, onRemove }: IListInputRowProps) {
  const { setNodeRef, style, handleProps } = useSortableRow(id)
  const [editing, setEditing] = useState<string | null>(null)

  const commit = () => {
    // An emptied item keeps its text: removing is the trash button's job.
    if (editing !== null && editing.trim() !== '') onSave(editing.trim())
    setEditing(null)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    }
    if (e.key === 'Escape') {
      // Esc would also close the modal around the form.
      e.preventDefault()
      e.stopPropagation()
      setEditing(null)
    }
  }

  return (
    <Flex ref={setNodeRef} style={style} align="center" gap={1} pl={1} pr={2} py={1} bg="gray.50" borderRadius="md">
      <DragHandle {...handleProps} isDisabled={isDisabled} />
      {editing === null ? (
        <Text flex="1" minW={0} fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
          {text}
        </Text>
      ) : (
        <Input
          // Its own id: inside a FormField every Input would otherwise take the field's.
          id={`${id}-edit`}
          flex="1"
          size="sm"
          bg="white"
          autoFocus
          value={editing}
          maxLength={maxLength}
          onChange={(e) => setEditing(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          aria-label="Editar item"
        />
      )}
      {editing === null && (
        <ActionButton action="edit" label="Editar item" onClick={() => setEditing(text)} isDisabled={isDisabled} />
      )}
      <ActionButton action="delete" label="Remover item" onClick={onRemove} isDisabled={isDisabled} mr={0} />
    </Flex>
  )
}
